/* ============================================================
   TDHQ header behaviour — tdhq-header.js

   Everything here is progressive enhancement. Without JS: search still
   submits (real form + GET), every nav and mega-menu link still works
   (real anchors, panels rendered server-side), and the announcement bar
   is a swipeable rail. Nothing below is required to shop the site.

   No dependencies. Deferred in the layout.
   ============================================================ */
(function () {
  'use strict';

  /* Shopify's JSON endpoints hand back UNSIZED originals (1500-2000px). The CDN
     resizes on a `width` query parameter, so ask for what the box needs. Any
     width already on the URL is replaced rather than duplicated. */
  /* the site's one sized-picture helper (TdhqImg, layout head) */
  function sizedImage(url, w) { return window.TdhqImg.sized(url, w); }

  // Every tdhq- section includes nothing itself, but a section re-render in
  // the theme editor can re-run this file. Guard so listeners bind once.
  if (window.__tdhqHeaderLoaded) return;
  window.__tdhqHeaderLoaded = true;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Back to the top of the page, the tab bar's way (audit home-23, shop-21).
     600px is where the old floating button used to appear. */
  var TOP_TAP_MIN = 600;
  function toTop() {
    window.scrollTo({ top: 0, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  }

  /* ---------------------------------------------------------
     Analytics. Non-personal parameters only.
     The privacy rules forbid sending raw search text, customer names,
     emails or account data, so search events carry counts, never the
     query string itself.
     --------------------------------------------------------- */
  function track(name, params) {
    var detail = params || {};
    detail.device_type = window.innerWidth < 768 ? 'mobile'
                       : window.innerWidth < 1100 ? 'tablet' : 'desktop';
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(Object.assign({ event: name }, detail));
    }
    document.dispatchEvent(new CustomEvent('tdhq:' + name, { detail: detail }));
  }

  /* Any element carrying data-tdhq-event reports a click — contact routes,
     rewards CTAs, quick links. Non-personal: the method, a short label and
     the destination path, never form values or account data. */
  document.addEventListener('click', function (e) {
    var el = e.target.closest('[data-tdhq-event]');
    if (!el) return;
    var label = (el.getAttribute('aria-label') || el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 60);
    var href = el.getAttribute('href') || '';
    track(el.getAttribute('data-tdhq-event'), {
      contact_method: el.getAttribute('data-tdhq-method') || '',
      link_label: label,
      destination: href.indexOf('http') === 0 ? href.split('?')[0] : href
    });
  });

  /* ---------------------------------------------------------
     RE-BINDING AFTER A SECTION RE-RENDER

     The guard above stops this file's listeners being bound twice, and that is
     right - but on its own it made the theme editor WORSE, not better. Editing
     the header re-renders the section, Shopify swaps in brand new markup, and
     the file returns early: the merchant is left looking at a header whose
     mega menu, search, drawer and sticky state have no handlers at all, with
     no clue that a preview reload would fix it.

     So the per-element wiring is a function that can run again, and the
     document- and window-level listeners it registers are tracked and removed
     before each re-run. Without the tracking, re-binding would either stack up
     duplicate handlers or leave the old ones closed over elements that are no
     longer in the document.
     --------------------------------------------------------- */
  var bound = [];
  function on(target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    bound.push([target, type, fn, opts]);
  }
  function unbindGlobals() {
    for (var i = 0; i < bound.length; i++) {
      bound[i][0].removeEventListener(bound[i][1], bound[i][2], bound[i][3]);
    }
    bound = [];
  }

  var header = document.querySelector('[data-tdhq-header]');

  /* =========================================================
     1. STICKY COMPACT STATE
     Compact after ~120px. Never hide-and-reappear on scroll —
     an aggressively hiding header makes navigation feel unstable.
     ========================================================= */
  function sticky() {
    if (!header) { return; }
    var THRESHOLD = 120;
    var stuck = false;
    var queued = false;

    /* ----------------------------------------------------------------
       HOW TALL THE PINNED HEADER ACTUALLY IS, PUBLISHED TO THE PAGE.

       --h-main and --h-nav are declared on .tdhq-header-wrap, so page content
       cannot see them: every `top: calc(var(--h-main, 72px) + var(--h-nav,
       52px) + 24px)` in the stylesheets was silently resolving to its 148px
       FALLBACK, which is not the real height at any breakpoint - and elsewhere
       the offset was a hard-coded 96px, which is not the height at any
       breakpoint either. That is why the sticky filter panel and the sticky
       product gallery parked underneath the header instead of below it.

       Measuring is the only honest source: it accounts for the announcement
       bar scrolling away, the compact sticky state, the per-breakpoint logo
       and nav sizes, and any of them changing.
       ---------------------------------------------------------------- */
    var section = header.closest('.tdhq-header-section') || header;
    var rootStyle = document.documentElement.style;

    /* NO ROOT WRITES WHILE SCROLLING (owner 20 Oct 2026: on phones the header
       "stutters and clips" when scrolling down and back up).
       --tdhq-sticky-h and --tdhq-anchor-h live on <html>, so every write
       restyles the whole document: measured 260-290ms per write on the home
       page at phone speed (4x CPU, 6,000 elements). Audit MOT-04 had cut the
       per-frame writes to one per pin, but that one still froze the page as it
       left the top and again as it came back. Everything that reads
       --tdhq-sticky-h matters only while the header is pinned (the sticky
       gallery, sidebars, contents lists, scroll offsets), so it holds the
       PINNED height, written at load and when a real size change settles.
       --tdhq-anchor-h differs between the two states only where pinning
       shrinks the header in the page flow (desktop, tablet), so only there is
       it written at a pin or unpin; on a phone both states are the same height
       and nothing is written while the page scrolls. */
    /* Start from the values already in force (tdhq-core.css sets the phone ones), so a value that is already
       right is never written: the first write at load restyled the whole page for nothing (22 Oct 2026). */
    var rootNow = window.getComputedStyle(document.documentElement);
    var lastH = rootNow.getPropertyValue('--tdhq-sticky-h').trim(), lastA = rootNow.getPropertyValue('--tdhq-anchor-h').trim();
    var pinnedH = 0, fullH = 0, stuckH = 0, flowShrink = 0, settleTimer = 0;
    function write(px) {
      var v = Math.round(px) + 'px';
      if (v === lastH) { return; }
      lastH = v;
      rootStyle.setProperty('--tdhq-sticky-h', v);
    }
    /* WHERE AN IN-PAGE LINK LANDS (audit MOT-03). The browser fixes an anchor's
       destination at the click, while the header is still full height; it then
       pins and shrinks mid-scroll, so the target landed 52-56px lower than
       meant with a strip of the band above showing. --tdhq-anchor-h is the
       height the header WILL have once pinned, plus the part of its shrink
       that animates and so moves the page during the jump (the instant part
       is held by the margin in onScroll). Every scroll-margin-top reads it. */
    function publishAnchor() {
      var v = Math.round(stuck ? pinnedH : pinnedH + flowShrink) + 'px';
      if (v === lastA) { return; }
      lastA = v;
      rootStyle.setProperty('--tdhq-anchor-h', v);
    }
    /* The pinned and full heights, read synchronously with the header's
       transitions switched off (.is-measuring in tdhq-header.css), then the
       original state restored while they are still off, so nothing animates
       at page load. Pinned, the section parks its top one announcement-height
       above the viewport (a negative sticky top), so what shows is its height
       plus that top. */
    function measure(quiet) {
      var was = header.classList.contains('is-stuck');
      if (was) { header.classList.add('is-measuring'); header.classList.remove('is-stuck'); }
      var full = section.getBoundingClientRect().height;
      /* transitions back on before pinning, in both states, so the animated part
         of the shrink is measured again after a breakpoint change while pinned */
      header.classList.remove('is-measuring');
      header.classList.add('is-stuck');
      var instant = section.getBoundingClientRect().height;   /* transitions still at their start */
      header.classList.add('is-measuring');                     /* cancels them: the final state */
      var final = section.getBoundingClientRect().height;
      var top = parseFloat(getComputedStyle(section).top) || 0;
      flowShrink = Math.max(0, Math.round(instant - final));
      if (!was) { header.classList.remove('is-stuck'); }
      void section.offsetHeight;   /* settle the restored state while transitions are still off */
      header.classList.remove('is-measuring');
      fullH = Math.round(full);
      stuckH = Math.round(final);
      pinnedH = Math.round(final + Math.min(0, top));
      if (!quiet) { publishAnchor(); }
    }
    /* A size change that is not the pin itself (the logo arriving, a longer
       announcement) is measured again once it settles; the pin's own change is
       the height already expected and writes nothing. */
    function checkSize() {
      var h = section.getBoundingClientRect().height;
      if (Math.abs(h - (stuck ? stuckH : fullH)) <= 1) { return; }
      measure();
      write(pinnedH);
    }
    function publishSettled() { clearTimeout(settleTimer); settleTimer = setTimeout(checkSize, 240); }

    /* HYSTERESIS - WHY THE PAGE BOUNCED.
       The header is in the page flow (a sticky section), and the compact state
       makes it 36px shorter on desktop and up to ~64px on a phone (the search
       row folds away). The browser's scroll anchoring keeps the content still
       when something above it shrinks, by pulling scrollY back by the same
       amount - so one threshold at 120px flipped itself: stick at 121, scrollY
       drops to 85, unstick, the header grows, scrollY is pushed to 121, stick
       again... the whole page shook up and down between 120 and 156px (the
       client's recording, 2026-09-22).
       Compact past 120; expand again only once the page is back above
       (120 - shrink - 24), where shrink is MEASURED at each toggle. The band
       between the two is wider than any jump the toggle itself can cause, so
       the state can never undo itself. */
    var shrink = 80;
    var searchBtn = header.querySelector('[data-tdhq-search-toggle]');
    function onScroll() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        var y = window.scrollY;
        var should = stuck ? y > Math.max(0, THRESHOLD - shrink - 24) : y > THRESHOLD;
        if (should !== stuck) {
          var before = section.getBoundingClientRect().height;
          stuck = should;
          header.classList.toggle('is-stuck', stuck);
          /* Back at the top the full search row is there again, so the row the
             pinned search icon opened is closed and the icon no longer says it
             is expanded (audit chrome-01). */
          if (!stuck && header.classList.contains('is-search-open')) {
            header.classList.remove('is-search-open');
            if (searchBtn) { searchBtn.setAttribute('aria-expanded', 'false'); }
          }
          var delta = Math.abs(before - section.getBoundingClientRect().height);
          if (delta > 0) { shrink = Math.round(delta); }
          /* the part of the shrink that happens at once (the phone search row
             folding away) is held as a margin, so nothing below the header
             jumps up 64px after the browser has fixed a scroll destination */
          section.style.marginBottom = stuck && delta > 0 ? Math.round(delta) + 'px' : '';
          publishAnchor();   /* writes only where the two states differ (see above) */
        }
      });
    }
    on(window, 'scroll', onScroll, { passive: true });
    /* the header's heights follow the width only: a phone's address bar showing
       or hiding (height only, at every change of scroll direction) is skipped */
    var lastW = window.innerWidth;
    on(window, 'resize', function () {
      if (window.innerWidth === lastW) { return; }
      lastW = window.innerWidth;
      clearTimeout(settleTimer);
      settleTimer = setTimeout(function () { measure(); write(pinnedH); }, 240);
    });
    if (window.ResizeObserver) { new ResizeObserver(publishSettled).observe(section); }
    /* First paint: every read first (both heights and the band), then both root
       variables together. A :root variable re-styles the whole page, so a write
       between two reads cost a second whole-page style pass. */
    measure(true);
    publishAnchor();
    write(pinnedH);
    onScroll();
  }

  /* =========================================================
     2. MEGA MENU
     Opens on hover (with intent delay), click, or keyboard.
     ========================================================= */
  function megaMenu() {
    var items = document.querySelectorAll('[data-tdhq-nav-item]');
    if (!items.length) return;

    // Instant hover opening makes the menu flicker as the pointer crosses
    // the nav bar, so we wait for intent. On the way out we use a longer
    // grace period: without it the menu closes whenever the pointer clips
    // the 1px boundary between the nav bar and the panel.
    var OPEN_DELAY = 300;
    var CLOSE_DELAY = 220;
    var openTimer = null;
    var closeTimer = null;
    var current = null;

    function panelOf(item) { return item.querySelector('[data-tdhq-mega]'); }
    function toggleOf(item) { return item.querySelector('[data-tdhq-nav-toggle]'); }

    function open(item) {
      if (current === item) return;
      /* Switching menus DOES close the previous one even when focus is inside
         it - close() hands focus back to that menu's toggle first, so nothing
         is stranded. Skipping the close here instead would leave the old panel
         open with `current` no longer pointing at it, and its focusout guard
         (`current === item`) would then never fire: a panel open for ever. */
      /* Moving straight from one department to the next swaps the panels with
         no fade either way. Fading here flashed the page through the menu on
         every hover along the bar; a menu opened from nothing, or closed,
         still fades (.tdhq-mega--instant in tdhq-header.css). */
      var switching = !!current;
      if (current) close(current, true);
      var panel = panelOf(item), toggle = toggleOf(item);
      if (!panel || !toggle) return;
      panel.classList.toggle('tdhq-mega--instant', switching);
      panel.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      item.classList.add('tdhq-nav__item--open');
      current = item;

      /* Wake the promo image. It ships loading="lazy" — correct, because eager
         would pull six mega-menu images on every page load and compete with the
         hero for LCP. But a lazy image inside a [hidden] panel is never near the
         viewport, so the browser defers it and does NOT reliably re-evaluate when
         the panel is revealed: the promo card rendered its heading and body text
         with a blank space where the picture should be, on every panel.
         Proved it was deferral and not a bad URL: an independent `new Image()`
         with the same src loaded at 760px, and flipping this one to eager made it
         appear immediately. Flipping on first open costs nothing — the fetch only
         happens once the customer actually opens that menu. */
      var lazies = panel.querySelectorAll('img[loading="lazy"]');
      for (var i = 0; i < lazies.length; i++) lazies[i].setAttribute('loading', 'eager');
      var link = item.querySelector('.tdhq-nav__link');
      track('mega_menu_open', { menu_name: link ? link.textContent.trim().toLowerCase() : '' });
    }

    function close(item, instant) {
      var panel = panelOf(item), toggle = toggleOf(item);
      /* Never hide the element that currently holds focus. `display: none` on
         an ancestor drops the focused node out of the tree, and the browser
         resets focus to <body> - the ring vanishes, and the next Tab restarts
         from the top of the document. Hand focus back to the toggle first, so
         the customer resumes from where the menu was. */
      if (panel && panel.contains(document.activeElement) && toggle) toggle.focus();
      if (panel) {
        panel.classList.toggle('tdhq-mega--instant', !!instant);
        panel.hidden = true;
      }
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      item.classList.remove('tdhq-nav__item--open');
      if (current === item) current = null;
    }

    /* Hover must not close a panel the KEYBOARD is inside. The pointer wandering
       off the nav is not a signal about someone who is tabbing through the
       panel's links - and on a hybrid machine (touchpad nudge, tab-navigating
       user) the two happen at once. */
    function holdsFocus(item) { return !!item && item.contains(document.activeElement); }

    function closeAll() {
      /* Not forEach(close): forEach passes the index as the second argument,
         which close() would read as "switch instantly". */
      items.forEach(function (it) { close(it); });
      current = null;
    }

    function clearTimers() {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
    }

    items.forEach(function (item) {
      var toggle = toggleOf(item);

      item.addEventListener('mouseenter', function () {
        clearTimers();
        // If a menu is already open, switch immediately — the customer has
        // clearly demonstrated intent to browse the nav bar.
        openTimer = setTimeout(function () { open(item); }, current ? 60 : OPEN_DELAY);
      });

      item.addEventListener('mouseleave', function () {
        clearTimers();
        closeTimer = setTimeout(function () {
          if (holdsFocus(item)) return;   // the keyboard is still in there
          close(item);
        }, CLOSE_DELAY);
      });

      if (toggle) {
        toggle.addEventListener('click', function (e) {
          e.preventDefault();
          clearTimers();
          if (current === item) { close(item); } else { open(item); }
        });
      }

      // Focus leaving the item entirely closes it. relatedTarget is null when
      // focus moves to the browser chrome, which should not close the menu
      // mid-interaction, so we re-check on the next tick instead.
      item.addEventListener('focusout', function () {
        setTimeout(function () {
          if (current === item && !item.contains(document.activeElement)) close(item);
        }, 0);
      });

      // Track which category routes are actually used, so weak columns can
      // be pruned later using real behaviour rather than opinion.
      var panel = panelOf(item);
      if (panel) {
        panel.addEventListener('click', function (e) {
          var a = e.target.closest('a');
          if (!a) return;
          var col = a.closest('.tdhq-mega__col');
          var colTitle = col ? col.querySelector('.tdhq-mega__col-title') : null;
          track('mega_menu_click', {
            link_name: a.textContent.trim(),
            menu_column: colTitle ? colTitle.textContent.trim() : '',
            destination: a.getAttribute('href') || ''
          });
        });
      }
    });

    on(document, 'keydown', function (e) {
      if (e.key !== 'Escape' || !current) return;
      var item = current;
      var toggle = toggleOf(item);
      close(item);
      // Return focus to the control that opened the panel.
      if (toggle) toggle.focus();
    });

    on(document, 'click', function (e) {
      if (current && !current.contains(e.target)) closeAll();
    });
  }

  /* =========================================================
     3. ANNOUNCEMENT ROTATION (mobile)
     One message at a time. Pauses on interaction, never runs for
     reduced-motion visitors, and stays swipeable either way.
     ========================================================= */
  function announce() {
    var track_ = document.querySelector('[data-tdhq-announce-track]');
    if (!track_ || !header) return;

    var items = track_.children;
    if (items.length < 2) return;

    var delay = parseInt(header.getAttribute('data-rotate'), 10) || 6000;
    var i = 0;
    var timer = null;
    var paused = false;

    function isMobileRail() {
      // Only rotate while the track is actually a one-up rail; on desktop all
      // three messages are visible at once and scrolling it would be wrong.
      return track_.scrollWidth > track_.clientWidth + 4;
    }

    function step() {
      if (paused || document.hidden || !isMobileRail()) return;
      i = (i + 1) % items.length;
      track_.scrollTo({ left: items[i].offsetLeft - track_.offsetLeft, behavior: 'smooth' });
    }

    function start() {
      if (reduceMotion.matches) return;
      stop();
      timer = setInterval(step, delay);
    }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }

    // Any manual interaction hands control to the customer permanently for
    // this page view — resuming would fight the person swiping.
    ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach(function (ev) {
      track_.addEventListener(ev, function () { paused = true; stop(); }, { passive: true });
    });
    track_.addEventListener('mouseenter', function () { paused = true; });
    track_.addEventListener('mouseleave', function () { paused = false; });

    on(document, 'visibilitychange', function () {
      if (document.hidden) { stop(); } else if (!paused) { start(); }
    });
    reduceMotion.addEventListener('change', function () {
      if (reduceMotion.matches) { stop(); } else if (!paused) { start(); }
    });

    start();
  }

  /* =========================================================
     4. PREDICTIVE SEARCH
     ========================================================= */
  function search() {
    var roots = document.querySelectorAll('[data-tdhq-search]');
    if (!roots.length) return;

    var MIN_CHARS = 2;
    var DEBOUNCE = 220;
    var LIMITS = { product: 6, collection: 4, brand: 4, article: 3 };

    /* Brief section 6 asks for Brands as a group of its own, between Collections
       and the guides. /search/suggest.json has no notion of a brand, so the set
       of brand collections is read back from the Brands mega panel this same
       header already renders on every page (snippets/tdhq-brand-columns.liquid),
       where every link has already been proved to be a real, stocked collection.
       Reading markup that is already in the document costs nothing; resolving
       the brands a second time in Liquid would mean another pass over the brand
       metaobjects on every page load, which section 22 rules out. The set is
       built on the first search, never on page load. A brand collection that is
       not in the curated menu simply stays under Collections — the right
       destination under a less specific heading. */
    var brandSet = null;
    function collectionHandle(url) {
      var rest = url ? String(url).split('/collections/')[1] : '';
      if (!rest) return '';
      /* suggest.json returns tracking parameters on these URLs
         (/collections/carpro?_pos=1&_psq=carpro...), so strip them. */
      return rest.split('?')[0].split('#')[0].split('/')[0];
    }
    function isBrandCollection(url) {
      if (brandSet === null) {
        brandSet = {};
        var links = document.querySelectorAll('.tdhq-mega--brands .tdhq-mega__columns a[href]');
        for (var i = 0; i < links.length; i++) {
          var h = collectionHandle(links[i].getAttribute('href'));
          if (h) brandSet[h] = true;
        }
      }
      var ch = collectionHandle(url);
      return !!ch && brandSet[ch] === true;
    }

    var currencyFmt = null;
    try {
      currencyFmt = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP'
      });
    } catch (e) { /* Intl unavailable: fall back to the raw string below. */ }

    // Shopify's predictive-search JSON has returned `price` both as a decimal
    // string ("19.95") and as minor units ("1995") across API versions, so
    // normalise defensively rather than trusting one shape.
    function money(raw) {
      if (raw == null || raw === '') return '';
      var s = String(raw).replace(/[^0-9.\-]/g, '');
      if (!s) return String(raw);
      var n = parseFloat(s);
      if (isNaN(n)) return String(raw);
      if (s.indexOf('.') === -1 && Math.abs(n) >= 100) n = n / 100;
      return currencyFmt ? currencyFmt.format(n) : n.toFixed(2);
    }

    function el(tag, cls, text) {
      var n = document.createElement(tag);
      if (cls) n.className = cls;
      if (text != null) n.textContent = text;
      return n;
    }

    /* Titles as the server prints them: no en or em dashes (the client's copy
       rule). The same chain as the Liquid title outputs and cleanTitle() in
       tdhq-cart-drawer.js, so a title reads the same in the suggestions, on the
       results page and in the basket (audit chrome-06: the suggestions printed
       'Tyre Gloss Gel', an en dash, 'High-Gloss Finish'; the cards print a
       hyphen). */
    function cleanTitle(s) {
      return String(s || '').split(' \u2013 ').join(' - ').split(' \u2014 ').join(' - ').split('\u2014').join(' - ').split('\u2013').join('-');
    }

    roots.forEach(function (root) {
      var input = root.querySelector('[data-tdhq-search-input]');
      var panel = root.querySelector('[data-tdhq-search-panel]');
      var status = root.querySelector('[data-tdhq-search-status]');
      if (!input || !panel) return;

      var timer = null;
      var controller = null;
      var results = [];
      var active = -1;

      function hide() {
        /* ABORT FIRST. Dismissing the panel has to cancel the request behind
           it, or a response that was already on the wire lands a second later
           and render() puts the panel back - showing suggestions for text the
           shopper has deleted, above an empty search box, after the combobox
           has already announced itself as collapsed. */
        if (controller) { controller.abort(); controller = null; }
        clearTimeout(timer);
        panel.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
        active = -1;
      }

      function setActive(next) {
        if (!results.length) return;
        if (active > -1) results[active].removeAttribute('aria-selected');
        active = (next + results.length) % results.length;
        var node = results[active];
        node.setAttribute('aria-selected', 'true');
        input.setAttribute('aria-activedescendant', node.id);
        // Keep the highlighted row inside the scroll area without yanking the
        // whole page around.
        node.scrollIntoView({ block: 'nearest' });
      }

      /* A listbox may own only `option` and `group` children. These wrappers
         were plain divs, so every option in the panel was orphaned from the
         combobox as far as the accessibility tree was concerned: the input's
         aria-activedescendant pointed at a node the listbox did not own, and
         "Products, 6 items" was never announced. role=group + aria-label fixes
         ownership and gives the section a name; the visible <p> is hidden from
         assistive tech because the group label already says the same words. */
      function group(title) {
        var g = el('div', 'tdhq-search__group');
        g.setAttribute('role', 'group');
        g.setAttribute('aria-label', title);
        var t = el('p', 'tdhq-search__group-title', title);
        t.setAttribute('aria-hidden', 'true');
        g.appendChild(t);
        return g;
      }

      /* A row with no picture of its own shows the site's glyph for its
         kind (grid, tag, book, bag), cloned from the template the search
         snippet renders, instead of an empty grey square (audit
         chrome-06). The box stays, so every title starts on one line. */
      function addGlyph(media, type) {
        var glyph = root.querySelector('template[data-tdhq-search-icons]');
        var g = glyph && (glyph.content.querySelector('[data-kind="' + type + '"]') || glyph.content.querySelector('[data-kind="collection"]'));
        if (g) {
          media.className += ' tdhq-search__result-media--icon';
          media.appendChild(g.firstElementChild.cloneNode(true));
        }
      }
      function putImage(media, src) {
        var img = el('img');
        /* 44px box: 1x and 2x candidates, never the 1500px original. */
        img.src = sizedImage(src, 88);
        img.srcset = sizedImage(src, 44) + ' 44w, ' + sizedImage(src, 88) + ' 88w, ' + sizedImage(src, 132) + ' 132w';
        img.sizes = '44px';
        img.alt = '';
        img.width = 44; img.height = 44;
        img.loading = 'lazy';
        media.appendChild(img);
      }

      /* COLLECTION PICTURES (22 Oct 2026, owner). suggest.json sends a
         collection's own picture only, and the category collections have
         none, so their rows showed the grid glyph. sections/tdhq-search-thumbs
         answers, for the same words, each collection's own picture or else its
         newest product's photo (the rule of every collection tile on the site);
         query() asks for it beside suggest.json. A row waiting on it keeps its
         empty box for that moment, then takes the photo, or the glyph when
         there is none. Pictures already seen are kept for the next keystroke. */
      var thumbCache = {};
      var pendingThumbs = [];
      function readThumbs(html) {
        var m = /<script[^>]*data-tdhq-search-thumbs[^>]*>([\s\S]*?)<\/script>/.exec(html || '');
        if (!m) { return null; }
        try { return JSON.parse(m[1]); } catch (e) { return null; }
      }
      /* PRODUCT ROWS IN THE RESULTS PAGE'S ORDER (28 Oct 2026, owner: "bad
         suggestions"). suggest.json ranks a product by how its title starts,
         so "snow foam" offered six Snow Foam Lance Adapters and no snow foam
         (all ten it had were adapters); /search ranks the snow foams first,
         half-typed words too. sections/tdhq-search-products answers the top
         six of /search for the same words; query() asks for it beside
         suggest.json. Its products lead the Products group and suggest.json's
         own fill any room left, so a search /search cannot answer still
         shows what it did before. */
      function readProducts(html) {
        var m = /<script[^>]*data-tdhq-search-products[^>]*>([\s\S]*?)<\/script>/.exec(html || '');
        if (!m) { return null; }
        try { return JSON.parse(m[1]); } catch (e) { return null; }
      }
      function productPath(url) {
        return String(url || '').split('?')[0].split('#')[0];
      }
      function productList(ranked, suggested) {
        var out = [];
        var seen = {};
        [ranked || [], suggested || []].forEach(function (list) {
          list.forEach(function (p) {
            var key = p && productPath(p.url);
            if (!key || seen[key] || out.length >= LIMITS.product) { return; }
            seen[key] = true;
            out.push(p);
          });
        });
        return out;
      }

      function collectionRow(type, c, thumbs) {
        var own = (c.featured_image && c.featured_image.url) || '';
        var h = collectionHandle(c.url);
        var known = !own && h && thumbCache[h];
        return {
          type: type, url: c.url, title: c.title,
          image: own || known || '',
          productPhoto: !!known,
          waitThumb: (!own && !known && thumbs && h) ? h : ''
        };
      }

      // Built with DOM methods rather than innerHTML: these strings come from
      // product and article titles, and building markup by concatenation is
      // how stored content becomes an injection vector.
      function render(data, term, thumbs, ranked) {
        panel.textContent = '';
        results = [];
        active = -1;
        pendingThumbs = [];

        var idx = 0;
        function addRow(parent, opts) {
          var a = el('a', 'tdhq-search__result');
          a.id = 'tdhq-sr-' + (input.id || 'x') + '-' + (idx++);
          a.href = opts.url;
          a.setAttribute('role', 'option');

          var media = el('span', 'tdhq-search__result-media');
          if (!opts.image && opts.waitThumb) {
            pendingThumbs.push({ media: media, handle: opts.waitThumb, type: opts.type });
          } else if (!opts.image) {
            addGlyph(media, opts.type);
          }
          /* a logo sits whole on white; a product photo fills the box as in the Products rows */
          if (opts.image && !opts.productPhoto && (opts.type === 'collection' || opts.type === 'brand')) {
            media.className += ' tdhq-search__result-media--contain';
          }
          if (opts.image) {
            putImage(media, opts.image);
          }
          a.appendChild(media);

          var mid = el('span');
          mid.appendChild(el('span', 'tdhq-search__result-title', cleanTitle(opts.title)));
          if (opts.sub) mid.appendChild(el('span', 'tdhq-search__result-brand', opts.sub));
          a.appendChild(mid);

          var right = el('span', 'tdhq-search__result-meta');
          if (opts.price) right.appendChild(el('span', 'tdhq-search__result-price', opts.price));
          if (opts.stock) {
            right.appendChild(el('span',
              'tdhq-search__result-stock' + (opts.available ? '' : ' tdhq-search__result-stock--out'),
              opts.stock));
          }
          a.appendChild(right);

          a.addEventListener('click', function () {
            track('search_result_click', { result_type: opts.type, result_position: idx });
          });

          parent.appendChild(a);
          results.push(a);
        }

        var r = (data && data.resources && data.resources.results) || {};
        var total = 0;

        var prods = productList(ranked, r.products);
        if (prods.length) {
          var g = group('Products');
          prods.forEach(function (p) {
            addRow(g, {
              type: 'product',
              url: p.url,
              title: window.tdhqName ? window.tdhqName(p.title) : p.title,
              sub: p.vendor || '',
              price: money(p.price),
              available: p.available !== false,
              /* Brief section 6: every product result shows an availability
                 status, not only the ones that are out of stock. "In stock" is
                 the wording the product card and the product page already use,
                 and it takes the neutral grey of the rest of the row so that
                 only the negative state is coloured — availability is never
                 carried by colour alone (brief section 21). When the API sends
                 no `available` field at all, nothing is claimed either way. */
              stock: p.available === false ? 'Out of stock' : (p.available === true ? 'In stock' : ''),
              image: (p.featured_image && p.featured_image.url) || p.image || ''
            });
          });
          panel.appendChild(g);
          total += prods.length;
        }

        if (r.collections && r.collections.length) {
          /* One pass, two groups, the API's relevance order kept inside each. */
          var plainCols = [];
          var brandCols = [];
          r.collections.forEach(function (c) {
            /* An old manual copy renamed <handle>-legacy (Store Structure
               Plan A3) stays published until the admin unpublishes it; its
               automatic twin holds the real handle and is the one to offer. */
            if (/-legacy$/.test(collectionHandle(c.url))) return;
            if (isBrandCollection(c.url)) { brandCols.push(c); } else { plainCols.push(c); }
          });

          if (plainCols.length) {
            var gc = group('Collections');
            plainCols.slice(0, LIMITS.collection).forEach(function (c) {
              addRow(gc, collectionRow('collection', c, thumbs));
            });
            panel.appendChild(gc);
            total += Math.min(plainCols.length, LIMITS.collection);
          }

          /* Brands sits AFTER Collections, matching the priority order the
             brief states: exact products, relevant collections, brand pages,
             useful guides. A group with nothing in it is not rendered. */
          if (brandCols.length) {
            var gb = group('Brands');
            brandCols.slice(0, LIMITS.brand).forEach(function (c) {
              addRow(gb, collectionRow('brand', c, thumbs));
            });
            panel.appendChild(gb);
            total += Math.min(brandCols.length, LIMITS.brand);
          }
        }

        if (r.articles && r.articles.length) {
          var ga = group('Advice & guides');
          r.articles.slice(0, LIMITS.article).forEach(function (a2) {
            addRow(ga, {
              type: 'article',
              url: a2.url,
              title: a2.title,
              image: (a2.image && a2.image.url) || a2.image || ''
            });
          });
          panel.appendChild(ga);
          total += Math.min(r.articles.length, LIMITS.article);
        }

        if (!total) {
          /* Never a dead end (audit chrome-11): the full results page for the
             same words, the whole range, and the advice centre. The two routes
             come from the theme (routes and the Advice Centre route setting)
             via the search snippet, so nothing here is typed twice. They are
             options like any other row, so the arrow keys reach them. */
          panel.appendChild(el('p', 'tdhq-search__empty', 'No matches found. Try a broader term, or:'));
          var ways = [['/search?options%5Bunavailable_products%5D=last&q=' + encodeURIComponent(term), 'See all results for \u201c' + term + '\u201d'],
                      [root.getAttribute('data-all-url'), 'Shop all products'],
                      [root.getAttribute('data-advice-url'), 'Car care advice']];
          ways.forEach(function (w, i) {
            if (!w[0]) { return; }
            var way = el('a', 'tdhq-search__way', w[1]);
            way.href = w[0];
            way.id = 'tdhq-sr-' + (input.id || 'x') + '-way' + i;
            way.setAttribute('role', 'option');
            panel.appendChild(way);
            results.push(way);
          });
        } else {
          /* This is the escape hatch from six suggestions to the full result
             page, and it used to be an anonymous <a> hanging directly off the
             listbox: invalid as a listbox child, and unreachable by ArrowDown,
             so a keyboard user could only get to it by tabbing back out. It is
             an option like any other row now, and joins `results` LAST so it
             sits at the end of the arrow order. */
          var all = el('a', 'tdhq-search__all', 'View all results for “' + term + '”');
          all.href = '/search?options%5Bunavailable_products%5D=last&q=' + encodeURIComponent(term);
          all.id = 'tdhq-sr-' + (input.id || 'x') + '-all';
          all.setAttribute('role', 'option');
          panel.appendChild(all);
          results.push(all);
        }

        if (pendingThumbs.length) {
          var waiting = pendingThumbs;
          thumbs.then(function (map) {
            waiting.forEach(function (w) {
              var src = map && map[w.handle];
              if (src) { thumbCache[w.handle] = src; putImage(w.media, src); } else { addGlyph(w.media, w.type); }
            });
          });
        }

        panel.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        // Announce a count, never the query or the titles.
        if (status) status.textContent = total ? total + ' suggestions available' : 'No matches found';
        track('search_suggest', { result_count: total });
      }

      function query(term) {
        if (controller) controller.abort();
        controller = new AbortController();
        var signal = controller.signal;

        var url = '/search/suggest.json'
          + '?q=' + encodeURIComponent(term)
          + '&resources[type]=product,collection,article'
          /* Six of EACH type (limit_scope=each). Without the scope the limit is
             a total across every type, so 'wax' came back as 4 products, 3
             collections and 1 guide although the panel is built for 6 products
             (audit chrome-10). Six collections feed two groups, Collections and
             Brands, up to four each; guides are sliced to 3 when rendered, and
             no image is fetched for a row that is never drawn (brief 22). */
          + '&resources[limit]=6&resources[limit_scope]=each'
          + '&resources[options][unavailable_products]=last';

        /* the collections' pictures, asked for at the same time (see collectionRow) */
        var thumbs = fetch('/search/suggest?q=' + encodeURIComponent(term)
            + '&resources[type]=collection&resources[limit]=6&resources[limit_scope]=each'
            + '&section_id=tdhq-search-thumbs', { signal: controller.signal })
          .then(function (res) { return res.ok ? res.text() : ''; })
          .then(readThumbs)
          .catch(function () { return null; });

        /* the products in the results page's order (see readProducts) */
        var ranked = fetch('/search?q=' + encodeURIComponent(term)
            + '&type=product&options%5Bunavailable_products%5D=last'
            + '&section_id=tdhq-search-products', { signal: signal })
          .then(function (res) { return res.ok ? res.text() : ''; })
          .then(readProducts)
          .catch(function () { return null; });

        fetch(url, { signal: signal, headers: { 'Accept': 'application/json' } })
          .then(function (res) { return res.ok ? res.json() : Promise.reject(res.status); })
          .then(function (data) {
            return ranked.then(function (list) {
              /* hide() or a newer keystroke aborted this request while it
                 waited for the ranked products: never paint it (see hide). */
              if (signal.aborted) { return; }
              render(data, term, thumbs, list);
            });
          })
          .catch(function (err) {
            if (err && err.name === 'AbortError') return;
            // Search must never become a dead end: fall back to the real
            // results page rather than showing a broken panel.
            hide();
          });
      }

      input.addEventListener('input', function () {
        var term = input.value.trim();
        clearTimeout(timer);
        if (term.length < MIN_CHARS) { hide(); return; }
        timer = setTimeout(function () { query(term); }, DEBOUNCE);
      });

      /* Back into a field that still holds a search: the suggestions come back
         (audit chrome-11: a click outside closed them, and clicking into the
         same words again showed nothing until a key was pressed). */
      input.addEventListener('focus', function () {
        var term = input.value.trim();
        if (term.length >= MIN_CHARS && panel.hidden) { query(term); }
      });

      input.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { hide(); return; }
        if (panel.hidden || !results.length) return;

        if (e.key === 'ArrowDown') { e.preventDefault(); setActive(active + 1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(active - 1); }
        else if (e.key === 'Enter' && active > -1) {
          // Only intercept Enter when a suggestion is highlighted; otherwise
          // the form submits normally to the full results page.
          e.preventDefault();
          results[active].click();
        }
      });

      on(document, 'click', function (e) {
        if (!root.contains(e.target)) hide();
      });
    });
  }

  /* =========================================================
     5. MOBILE SEARCH TOGGLE
     ========================================================= */
  function searchToggle() {
    var btn = document.querySelector('[data-tdhq-search-toggle]');
    if (!btn || !header) return;
    btn.addEventListener('click', function () {
      var open = header.classList.toggle('is-search-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      /* The row is shown by tdhq-header.css (.is-stuck.is-search-open), not by
         an inline display, which outlived the pinned state: back at the top the
         row kept display:block and the icon kept aria-expanded="true". sticky()
         closes it when the header un-pins. */
      if (open) {
        var input = header.querySelector('.tdhq-header__search-row [data-tdhq-search-input]');
        if (input) input.focus();
      }
    });
  }

  /* =========================================================
     5b. PHONE SEARCH MODE (client, 25 Sep 2026: "smooth as native phone
     app"). With the tab bar on, the phone's one header row is logo + field.
     Tapping the field is the app's search screen (.is-searching, see
     tdhq-header.css): the logo slides away, the field takes the row, Cancel
     slides in, the page dims below the row and the suggestions open as a
     full-width sheet. Cancel, Escape, a tap on the dimmed page or on a tab
     leaves it. --tdhq-search-top is where the row ends, re-read while the page
     moves under it (the announcement bar can still be scrolling away).
     ========================================================= */
  function appSearch() {
    if (!header) return;
    var bar = header.querySelector('.tdhq-header--hasbar');
    var root = header.querySelector('.tdhq-header__search-desktop [data-tdhq-search]');
    var row = header.querySelector('.tdhq-header__main');
    if (!bar || !root || !row) return;
    var input = root.querySelector('[data-tdhq-search-input]');
    var cancel = header.querySelector('[data-tdhq-search-cancel]');
    if (!input) return;
    var mq = window.matchMedia('(max-width: 899px)');
    var longPh = input.getAttribute('data-placeholder-long');
    var shortPh = input.getAttribute('data-placeholder-short');
    function placeholder() {
      if (longPh && shortPh) { input.setAttribute('placeholder', mq.matches ? shortPh : longPh); }
    }
    function place() {
      header.style.setProperty('--tdhq-search-top', Math.max(0, Math.round(row.getBoundingClientRect().bottom)) + 'px');
    }
    function searching() { return header.classList.contains('is-searching'); }
    function enter() {
      if (!mq.matches || searching()) return;
      place();
      header.classList.add('is-searching');
    }
    function leave() {
      if (!searching()) return;
      header.classList.remove('is-searching');
      /* the suggestions close the way Escape closes them (search() above) */
      input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
      input.blur();
    }
    input.addEventListener('focus', enter);
    input.addEventListener('keydown', function (e) { if (e.key === 'Escape') { leave(); } });
    if (cancel) { cancel.addEventListener('click', leave); }
    header.addEventListener('click', function (e) { if (e.target === header && searching()) { leave(); } });
    on(document, 'click', function (e) {
      if (searching() && e.target.closest && e.target.closest('.tdhq-tabbar')) { leave(); }
    });
    var queued = false;
    on(window, 'scroll', function () {
      if (!searching() || queued) { return; }
      queued = true;
      window.requestAnimationFrame(function () { queued = false; place(); });
    }, { passive: true });
    function onWidth() { placeholder(); if (!mq.matches) { leave(); } }
    if (mq.addEventListener) { on(mq, 'change', onWidth); }
    placeholder();
  }

  /* =========================================================
     6. DRAWER
     ========================================================= */
  function drawer() {
    var root = document.querySelector('[data-tdhq-drawer]');
    var scrim = document.querySelector('[data-tdhq-drawer-scrim]');
    /* querySelectorAll, not querySelector: the phone tab bar carries a second
       opener, and binding only the first one left the Shop tab doing nothing
       but follow its own href. */
    var openBtns = document.querySelectorAll('[data-tdhq-drawer-open]');
    if (!root || !openBtns.length) return;

    /* Every opener describes the same drawer, so they all report the same
       state: a stale aria-expanded on the tab bar would tell a screen reader
       the menu is closed while it is open. */
    function setExpanded(v) {
      Array.prototype.forEach.call(openBtns, function (b) { b.setAttribute('aria-expanded', v); });
    }

    var closeBtn = root.querySelector('[data-tdhq-drawer-close]');
    var titleEl = root.querySelector('[data-tdhq-drawer-title]');
    var panes = root.querySelectorAll('[data-tdhq-pane]');
    var returnFocus = null;

    /* ----------------------------------------------------------------
       HYDRATING THE DEEP PANES

       Everything below the drawer's top level ships inside a <template>
       (snippets/tdhq-drawer.liquid). Template content is parsed but is not in
       the document: 972 elements that used to exist on every page, on every
       device, for a menu most visits never open. This clones them in once, on
       the first open - synchronously, from markup already in the page, so
       there is no request to fail and no second copy of the menu to drift.

       Everything the drawer's own code touches is re-read afterwards, because
       `panes` was a static NodeList captured at init.
       ---------------------------------------------------------------- */
    function hydrate() {
      var tpl = root.querySelector('template[data-tdhq-drawer-deep]');
      if (!tpl) { return; }
      var body = root.querySelector('.tdhq-drawer__body') || tpl.parentNode;
      body.insertBefore(tpl.content.cloneNode(true), tpl);
      tpl.remove();
      panes = root.querySelectorAll('[data-tdhq-pane]');
      initBrandSearch();
    }

    /* The brand pane's search. This was an inline <script> beside the markup;
       inside a <template> it would never run, because template content is not
       executed. It is the same code, invoked after the clone. */
    function initBrandSearch() {
      var pane = root.querySelector('[data-tdhq-brands]');
      if (!pane || pane.dataset.ready) { return; }
      pane.dataset.ready = '1';
      var input = pane.querySelector('[data-tdhq-brandsearch]');
      var none = pane.querySelector('[data-tdhq-brandnone]');
      var groups = Array.prototype.slice.call(pane.querySelectorAll('[data-brand-group]'));
      var links = Array.prototype.slice.call(pane.querySelectorAll('[data-brand-link]'));
      function norm(str) { return (str || '').toLowerCase().replace(/[^a-z0-9]+/g, ''); }
      function searching() { return !!(input && norm(input.value).length >= 2); }
      // Brief 27: filter after two characters, ignore case and spacing.
      if (input) {
        /* Through morph(): a search that leaves a few brands shrinks the sheet,
           and clearing it grows the sheet back, so both glide. */
        input.addEventListener('input', function () { morph(function () {
          var q = norm(input.value), on = q.length >= 2, shown = 0;
          links.forEach(function (a) {
            var ok = !on || norm(a.dataset.brandName).indexOf(q) !== -1;
            a.hidden = !ok;
            if (ok && on) { shown++; }
          });
          groups.forEach(function (g) {
            if (!on) { g.hidden = false; g.open = false; return; }
            var any = g.querySelector('[data-brand-link]:not([hidden])');
            g.hidden = !any;
            g.open = !!any;
          });
          if (none) { none.hidden = !on || shown > 0; }
        }); });
      }
      // Brief 29: one group open at a time (native `name` grouping where
      // supported; this covers the rest), except while a search is open.
      groups.forEach(function (g) {
        g.addEventListener('toggle', function () {
          if (!g.open || searching()) { return; }
          groups.forEach(function (o) { if (o !== g) { o.open = false; } });
        });
      });
    }

    var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function visibleFocusables() {
      // Only the ACTIVE pane's controls are reachable. Without this filter Tab
      // walks into hidden panes and focus disappears off-screen.
      return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (n) {
        return n.offsetParent !== null;
      });
    }

    /* dir: 'fwd' when drilling in, 'back' for a Back row, nothing when the menu
       opens - the new screen then slides in from that side (audit chrome-05). */
    function show(name, dir) {
      var found = false;
      panes.forEach(function (p) {
        var match = p.getAttribute('data-tdhq-pane') === name;
        p.classList.toggle('is-active', match);
        p.classList.remove('is-enter-fwd', 'is-enter-back');
        if (match) {
          found = true;
          if (dir && !reduceMotion.matches) { p.classList.add(dir === 'back' ? 'is-enter-back' : 'is-enter-fwd'); }
          if (titleEl) {
            titleEl.textContent = p.getAttribute('data-tdhq-pane-title') || 'Menu';
          }
        }
      });
      if (!found) return;
      /* The first screen is headed by the logo, the others by their title
         (tdhq-header.css, .tdhq-drawer.is-root). */
      root.classList.toggle('is-root', name === 'root');
      root.querySelector('.tdhq-drawer__body').scrollTop = 0;

      /* Land on the TITLE of the screen just opened. visibleFocusables()[0] is
         the Close button in the drawer head - outside every pane - so drilling
         from Menu to Car Care to Shampoos put focus in the same place three
         times and announced "Close menu" each time, with no clue that the
         screen had changed at all. The title carries tabindex="-1" and names
         the dialog (aria-labelledby), so focusing it announces where you are.
         Falling back to the pane's own first control keeps a screen usable if
         the title is ever removed. */
      var landing = titleEl || Array.prototype.filter.call(
        root.querySelectorAll(FOCUSABLE),
        function (n) { return n.offsetParent !== null && n.closest('[data-tdhq-pane].is-active'); }
      )[0];
      if (landing) landing.focus();
    }

    /* THE SHEET GLIDES TO ITS NEW HEIGHT (audit chrome-05, re-checked). On a
       phone the menu is a bottom sheet as tall as the screen it shows
       (tdhq-header.css, "THE SHOP MENU IS A SHEET ABOVE THE TAB BAR"), so a
       drill or Back changed its height in one frame and the black head jumped:
       sheet top 201 -> 56px opening Brands at 390 (145px), 201 -> 514px
       opening Advice (313px), 381 -> 241px at 768, while the screen inside it
       already slid. change() makes the switch; the height is then tweened from
       the one on screen to the new one over --tdhq-t on --tdhq-ease, the same
       glide as the footer accordion. A tap mid-glide starts from where the
       sheet is, not from where it was heading. The full-height side drawer
       (900-1099px, or the tab bar switched off) does not change height, so
       nothing runs there. Reduced motion: the height just changes. */
    var sheetAnim = null;
    function morph(change) {
      var live = !root.hidden && root.classList.contains('is-open') &&
        typeof root.animate === 'function' && !reduceMotion.matches;
      var from = live ? root.getBoundingClientRect().height : 0;
      if (sheetAnim) { sheetAnim.cancel(); sheetAnim = null; }
      change();
      if (!live) { return; }
      var to = root.getBoundingClientRect().height;
      if (Math.abs(to - from) < 1) { return; }
      var cs = getComputedStyle(root);
      var a = root.animate({ height: [from + 'px', to + 'px'] }, {
        duration: window.TdhqMotion ? window.TdhqMotion.ms('--tdhq-t', 220) : 220,
        easing: cs.getPropertyValue('--tdhq-ease').trim() || 'cubic-bezier(0.4, 0, 0.2, 1)'
      });
      sheetAnim = a;
      a.onfinish = function () { if (sheetAnim === a) { sheetAnim = null; } };
    }

    /* The pending hide from a close. A re-open inside the 220ms slide-out
       used to be hidden by it a moment later: an invisible menu, the page
       still locked, and the Shop tab lit (audit chrome-03). */
    var closeTimer = null;

    function open() {
      clearTimeout(closeTimer);
      returnFocus = document.activeElement;
      /* Before anything is shown, so the first screen is complete and the
         drill-down targets exist by the time a row can be tapped. */
      hydrate();
      /* Wake the department and brand promo pictures now. They ship lazy, and
         a lazy image inside a hidden screen is only fetched once that screen is
         shown, so each promo was still an empty dark tile half a second after
         the screen opened (the same fix as the desktop mega menu). */
      var lazies = root.querySelectorAll('.tdhq-drawer__bpromo img[loading="lazy"]');
      for (var li = 0; li < lazies.length; li++) { lazies[li].setAttribute('loading', 'eager'); }
      root.hidden = false;
      if (scrim) scrim.hidden = false;
      // Force a frame so the transition runs from the off-screen position
      // rather than being skipped by the hidden -> visible change.
      requestAnimationFrame(function () {
        root.classList.add('is-open');
        if (scrim) scrim.classList.add('is-open');
      });
      document.documentElement.classList.remove('tdhq-drawer-closing');
      document.documentElement.classList.add('tdhq-drawer-open');
      setExpanded('true');
      morph(function () { show('root'); });
      if (titleEl) titleEl.textContent = 'Menu';
      track('drawer_open', {});
    }

    function close() {
      root.classList.remove('is-open');
      if (scrim) scrim.classList.remove('is-open');
      /* tdhq-drawer-closing keeps the phone tab bar in front until the sheet
         has gone (tdhq-header.css). Dropping the bar the moment the lock came
         off made the sheet slide down OVER it on close, hiding the bar for
         most of the 220ms, although it rises from BEHIND it on open; a tap
         on the Shop tab 60ms into the close landed on the sheet. */
      document.documentElement.classList.add('tdhq-drawer-closing');
      document.documentElement.classList.remove('tdhq-drawer-open');
      setExpanded('false');

      var done = function () {
        if (root.classList.contains('is-open')) { return; }   /* re-opened meanwhile */
        document.documentElement.classList.remove('tdhq-drawer-closing');
        root.hidden = true;
        if (scrim) scrim.hidden = true;
      };
      clearTimeout(closeTimer);
      if (reduceMotion.matches) { done(); } else { closeTimer = setTimeout(done, 220); }

      // Restore focus to whatever opened it.
      if (returnFocus && typeof returnFocus.focus === 'function') returnFocus.focus();
    }

    /* The tab bar's opener is an <a> with a real destination so it still goes
       somewhere with no JavaScript. Once the drawer is available that href
       must not also fire, or the drawer opens and the page navigates out from
       under it - which is exactly what it did. */
    /* On a phone the menu is a sheet above the tab bar, so the Shop tab that
       opened it is still under the thumb: a second tap closes it. On a page the
       Shop tab marks as current (a collection or a product), a tap after
       scrolling returns to the top first - a native tab bar's back-to-top,
       which replaces the floating button there - and the next tap opens the
       menu. */
    Array.prototype.forEach.call(openBtns, function (btn) {
      btn.addEventListener('click', function (e) {
        if (btn.tagName === 'A') { e.preventDefault(); }
        if (root.classList.contains('is-open')) { close(); return; }
        if (btn.classList.contains('is-current') && window.scrollY > TOP_TAP_MIN) { toTop(); return; }
        open();
      });
    });
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (scrim) scrim.addEventListener('click', close);
    root.addEventListener('animationend', function (e) {
      if (e.target.classList) { e.target.classList.remove('is-enter-fwd', 'is-enter-back'); }
    });

    root.addEventListener('click', function (e) {
      var drill = e.target.closest('[data-tdhq-drill]');
      if (drill) {
        e.preventDefault();
        morph(function () {
          show(drill.getAttribute('data-tdhq-drill'), drill.classList.contains('tdhq-drawer__back') ? 'back' : 'fwd');
        });
        return;
      }
      var a = e.target.closest('a[href]');
      if (a) track('drawer_click', { destination: a.getAttribute('href') || '' });
    });

    root.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key !== 'Tab') return;

      var f = visibleFocusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  /* =========================================================
     7. TAB BAR
     The tab for the page you are on scrolls back to the top instead of
     reloading the page (the Shop tab does the same inside drawer()). Any
     other tab closes an open menu sheet on its way, so the menu never stays
     up behind the saved panel or the next page.
     ========================================================= */
  function tabbar() {
    var bar = document.querySelector('[data-tdhq-tabbar]');
    if (!bar || bar.dataset.tdhqTabbarReady) return;
    bar.dataset.tdhqTabbarReady = '1';
    bar.addEventListener('click', function (e) {
      var tab = e.target.closest('.tdhq-tabbar__tab');
      if (!tab) return;
      /* ONE PANEL AT A TIME (chrome-07): the saved panel and the basket stay
         above a live tab bar, so a tab closes whichever of them it does not
         open on its way. */
      if (!tab.hasAttribute('data-tdhq-saved-open') && window.tdhqSavedClose) window.tdhqSavedClose();
      if (!tab.hasAttribute('data-tdhq-cart-link') && document.documentElement.classList.contains('tdhq-cd-open') &&
          window.tdhqCartDrawer && window.tdhqCartDrawer.close) window.tdhqCartDrawer.close();
      if (tab.hasAttribute('data-tdhq-drawer-open')) return;
      var sheet = document.querySelector('[data-tdhq-drawer].is-open');
      var shut = sheet && sheet.querySelector('[data-tdhq-drawer-close]');
      if (shut) shut.click();
      if (tab.classList.contains('is-current') && window.scrollY > 0) {
        e.preventDefault();
        toTop();
      }
    });
  }

  /* One entry point, so the editor can re-run exactly what a fresh page runs. */
  function boot() {
    unbindGlobals();
    header = document.querySelector('[data-tdhq-header]');
    sticky();
    /* The rest of the header sets up in steps (window.TdhqBoot, 21 Oct) so a swipe at page open is not held. */
    var rest = [megaMenu, announce, search, searchToggle, appSearch, drawer, tabbar];
    if (window.TdhqBoot) { window.TdhqBoot.steps(rest); } else { rest.forEach(function (fn) { fn(); }); }
  }

  boot();

  /* Shopify fires this when a section is re-rendered in the theme editor. Only
     re-boot when the section that changed actually contains the header or the
     drawer - every other section re-render is none of this file's business. */
  document.addEventListener('shopify:section:load', function (e) {
    var t = e.target;
    if (!t || !t.querySelector) { return; }
    if (t.querySelector('[data-tdhq-header], [data-tdhq-drawer]') ||
        (t.matches && t.matches('[data-tdhq-header], [data-tdhq-drawer]'))) {
      /* the bar's own line of script does not run in a re-rendered section,
         so its html classes are re-read from the new markup here, and the new
         bar is moved to <body> as that script does (the old one is dropped) */
      var bar = document.querySelector('[data-tdhq-tabbar]');
      if (t.querySelector('[data-tdhq-header]') || (t.matches && t.matches('[data-tdhq-header]'))) {
        bar = t.querySelector('[data-tdhq-tabbar]');
        Array.prototype.forEach.call(document.querySelectorAll('[data-tdhq-tabbar]'), function (old) {
          if (old !== bar && old.parentNode) { old.parentNode.removeChild(old); }
        });
        if (bar && bar.parentNode !== document.body) {
          var top = t;
          while (top.parentNode && top.parentNode !== document.body) { top = top.parentNode; }
          if (top.parentNode) { top.parentNode.insertBefore(bar, top.nextSibling); } else { document.body.appendChild(bar); }
        }
      }
      document.documentElement.classList.toggle('tdhq-has-tabbar', !!bar);
      document.documentElement.classList.toggle('tdhq-tab-current', !!(bar && bar.querySelector('.tdhq-tabbar__tab.is-current')));
      boot();
    }
  });
  /* The bar lives outside its section (tdhq-mobilebar.liquid), so it is not removed with it: when the editor
     deletes or re-renders the header section, the bar goes too (a re-render brings the new one in above). */
  document.addEventListener('shopify:section:unload', function (e) {
    var t = e.target;
    if (!t || !t.querySelector) { return; }
    if (!(t.querySelector('[data-tdhq-header]') || (t.matches && t.matches('[data-tdhq-header]')))) { return; }
    Array.prototype.forEach.call(document.querySelectorAll('[data-tdhq-tabbar]'), function (old) {
      if (old.parentNode) { old.parentNode.removeChild(old); }
    });
    document.documentElement.classList.remove('tdhq-has-tabbar', 'tdhq-tab-current');
  });
})();

/* ============================================================
   HQ REWARDS (Joy) - one handler for every [data-tdhq-joy] link.

   The client wants no floating launcher: HQ Rewards opens from the header,
   the phone menu, the account page and the rewards buttons. Joy's launcher is
   hidden in the app (Advanced > Display) and Joy opens a screen when the URL
   hash is one of its documented deeplinks (#joy-home, #joy-ways-to-earn,
   #joy-ways-to-redeem, #joy-rewards, #joy-activities, #joy-profile).

   Every entry point is a real link, so when Joy is not on the page (app off,
   script blocked) the click simply follows its href. When Joy is there the
   click becomes the deeplink instead: the phone menu is closed first, and a
   hash already in the address bar is cleared so the same screen can be
   opened twice in a row (a hash that does not change fires no hashchange).
   ============================================================ */
(function () {
  'use strict';
  if (window.__tdhqJoyLinks) return;
  window.__tdhqJoyLinks = true;

  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('[data-tdhq-joy]');
    if (!link || e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    /* Joy not on the page (app off, script blocked): follow the real link. */
    if (!window.joyInstance) return;
    e.preventDefault();
    var fallback = link.getAttribute('href');

    var sheet = document.querySelector('[data-tdhq-drawer].is-open');
    var shut = sheet && sheet.querySelector('[data-tdhq-drawer-close]');
    if (shut) shut.click();

    var hash = '#' + link.getAttribute('data-tdhq-joy');
    if (location.hash) history.replaceState(null, '', location.pathname + location.search);
    location.hash = hash;

    /* Joy draws its drawer on the first open, and not at all for a guest
       (the programme shows only after sign-in). Nothing open a moment later
       means the link's own destination is the right one. */
    setTimeout(function () {
      if (!document.querySelector('joy-loyalty-widget') && fallback) {
        history.replaceState(null, '', location.pathname + location.search);
        window.location.href = fallback;
      }
    }, 1200);
  });
})();

/* ============================================================
   PEEK SHEET - "How it works" without leaving the page.

   A link with data-tdhq-peek="<section anchor>" opens a sheet holding that
   section of its page: the heading, the intro and the numbered steps
   (tdhq-steps), plus the notice named by data-tdhq-peek-note (tdhq-notice).
   Shopify serves every storefront page with X-Frame-Options: DENY, so a page
   cannot be framed; it is fetched once instead and the section is read from
   it, which keeps the words the page's own, edited in one place. They are
   written as text, never as fetched markup (only the step icon, the theme's
   own svg, is copied). The foot keeps an Open full page button, and the
   link's href is the full page - which is also where a click goes if the
   page cannot be fetched or the section is not on it.
   ============================================================ */
(function () {
  'use strict';
  if (window.__tdhqPeek) return;
  window.__tdhqPeek = true;
  if (typeof HTMLDialogElement === 'undefined' || !window.fetch || !window.DOMParser) return;

  var root = document.documentElement;
  var pages = {};
  var dlg, titleEl, bodyEl, fullEl, opener = null;

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text) n.textContent = text;
    return n;
  }
  function txt(scope, sel) {
    var n = scope && scope.querySelector(sel);
    return n ? n.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  function build() {
    dlg = el('dialog', 'tdhq-peek');
    dlg.setAttribute('aria-labelledby', 'tdhq-peek-title');
    var bar = el('div', 'tdhq-peek__bar');
    titleEl = el('h2', 'tdhq-peek__title');
    titleEl.id = 'tdhq-peek-title';
    var close = el('button', 'tdhq-peek__close');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    close.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
    bar.appendChild(titleEl);
    bar.appendChild(close);
    bodyEl = el('div', 'tdhq-peek__body');
    var foot = el('div', 'tdhq-peek__foot');
    fullEl = el('a', 'tdhq-peek__full', 'Open full page');
    foot.appendChild(fullEl);
    dlg.appendChild(bar);
    dlg.appendChild(bodyEl);
    dlg.appendChild(foot);
    document.body.appendChild(dlg);
    close.addEventListener('click', function () { dlg.close(); });
    /* a click on the backdrop lands on the dialog element itself */
    dlg.addEventListener('click', function (e) { if (e.target === dlg) dlg.close(); });
    dlg.addEventListener('close', function () {
      root.classList.remove('tdhq-peek-open');
      if (opener && typeof opener.focus === 'function') opener.focus();
    });
  }

  /* INFORMATION CARDS (tdhq-card-grid): the delivery options and the store's facts
     open in the same peek as a how-it-works section. A card is a fact, not a
     step, so it carries no number; its value (an address, the opening hours) is
     copied with its own line breaks. */
  function renderCards(sec, frag) {
    var cards = sec.querySelectorAll('.tdhq-card');
    if (!cards.length) return null;
    var ul = el('ul', 'tdhq-peek__steps tdhq-peek__steps--cards'), seen = [];
    Array.prototype.forEach.call(cards, function (c) {
      var li = el('li', 'tdhq-peek__step');
      var svg = c.querySelector('.tdhq-card__icon svg');
      if (svg) {
        var ico = el('span', 'tdhq-peek__ico');
        ico.setAttribute('aria-hidden', 'true');
        ico.appendChild(document.importNode(svg, true));
        li.appendChild(ico);
      }
      var box = el('div', 'tdhq-peek__stepbody');
      var t = txt(c, '.tdhq-card__title');
      if (t) box.appendChild(el('h3', 'tdhq-peek__steptitle', t));
      var v = c.querySelector('.tdhq-card__value');
      if (v && v.textContent.trim()) {
        var vp = el('p', 'tdhq-peek__stepcopy tdhq-peek__value');
        Array.prototype.forEach.call(v.childNodes, function (n) { vp.appendChild(document.importNode(n, true)); });
        box.appendChild(vp);
      }
      var m = c.querySelector('.tdhq-card__meta');
      var mt = m ? m.textContent.replace(/\s+/g, ' ').trim() : '';
      if (mt && seen.indexOf(mt) < 0) {
        seen.push(mt);
        var mp = el('p', 'tdhq-peek__stepcopy tdhq-peek__lines');
        Array.prototype.forEach.call(m.childNodes, function (n) { mp.appendChild(document.importNode(n, true)); });
        box.appendChild(mp);
      }
      var cp = txt(c, '.tdhq-card__copy');
      if (cp) box.appendChild(el('p', 'tdhq-peek__stepcopy', cp));
      li.appendChild(box);
      ul.appendChild(li);
    });
    frag.appendChild(ul);
    return { heading: txt(sec, 'h2'), content: frag };
  }

  function render(doc, anchor, noteAnchor) {
    var sec = doc.getElementById(anchor);
    if (!sec) return null;
    var frag = document.createDocumentFragment();
    var lede = txt(sec, '.tdhq-heading__copy');
    if (lede) frag.appendChild(el('p', 'tdhq-peek__lede', lede));
    var steps = sec.querySelectorAll('.tdhq-step');
    if (!steps.length) return renderCards(sec, frag);
    var ol = el('ol', 'tdhq-peek__steps');
    Array.prototype.forEach.call(steps, function (s, i) {
      var li = el('li', 'tdhq-peek__step');
      li.appendChild(el('span', 'tdhq-peek__num', String(i + 1)));
      var svg = s.querySelector('.tdhq-step__icon svg');
      if (svg) {
        var ico = el('span', 'tdhq-peek__ico');
        ico.setAttribute('aria-hidden', 'true');
        ico.appendChild(document.importNode(svg, true));
        li.appendChild(ico);
      }
      var box = el('div', 'tdhq-peek__stepbody');
      var t = txt(s, '.tdhq-step__title');
      if (t) box.appendChild(el('h3', 'tdhq-peek__steptitle', t));
      var c = txt(s, '.tdhq-step__copy');
      if (c) box.appendChild(el('p', 'tdhq-peek__stepcopy', c));
      var items = s.querySelectorAll('.tdhq-step__list li');
      if (items.length) {
        var ul = el('ul', 'tdhq-peek__list');
        Array.prototype.forEach.call(items, function (it) {
          ul.appendChild(el('li', '', it.textContent.replace(/\s+/g, ' ').trim()));
        });
        box.appendChild(ul);
      }
      var n = txt(s, '.tdhq-step__note');
      if (n) box.appendChild(el('p', 'tdhq-peek__stepnote', n));
      li.appendChild(box);
      ol.appendChild(li);
    });
    frag.appendChild(ol);
    var foot = txt(sec, '.tdhq-steps__note');
    if (foot) frag.appendChild(el('p', 'tdhq-peek__footnote', foot));
    if (noteAnchor) {
      var ns = doc.getElementById(noteAnchor);
      var nt = txt(ns, '.tdhq-notice__title');
      var nc = txt(ns, '.tdhq-notice__copy');
      if (nt || nc) {
        var note = el('div', 'tdhq-peek__notice');
        note.setAttribute('role', 'note');
        if (nt) note.appendChild(el('strong', '', nt));
        var paras = ns.querySelectorAll('.tdhq-notice__copy p');
        if (paras.length) {
          Array.prototype.forEach.call(paras, function (p) {
            var pt = p.textContent.replace(/\s+/g, ' ').trim();
            if (pt) note.appendChild(el('p', '', pt));
          });
        } else if (nc) {
          note.appendChild(el('p', '', nc));
        }
        frag.appendChild(note);
      }
    }
    return { heading: txt(sec, 'h2'), content: frag };
  }

  /* U.30b (user 7 Oct 2026: "it says loading first and then it appears ...
     looks stupid"): the peek never shows a loading line. The page is fetched
     as soon as a visitor shows intent (pointer over the link, keyboard focus,
     a touch), and a click opens the popup only once its content is ready; a
     slow fetch shows a busy cursor on the link meanwhile. */
  function pageOf(link) {
    var href = link.getAttribute('href') || '';
    return href.split('#')[0] || location.pathname;
  }
  function load(page) {
    if (!pages[page]) {
      pages[page] = fetch(page, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (html) { return new DOMParser().parseFromString(html, 'text/html'); });
      pages[page].catch(function () { delete pages[page]; });
    }
    return pages[page];
  }

  /* U.30c ONE SECTION, NOT THE WHOLE PAGE. Shopify's section rendering returns
     just the section that holds the peek (under 1 KB against about 90 KB).
     The site's own peeks are seeded; any other is learned from its first
     full-page fetch and kept on this device. A section that is not there any
     more falls back to the full page, which also relearns its id. Section ids
     carry the template id, which is different in every theme: the seeds apply
     only on the theme they were read from (SEED_THEME, the live theme), and
     learned ids are kept per theme (U.58). */
  var THEME = String((window.Shopify && window.Shopify.theme && window.Shopify.theme.id) || '');
  var SEED_THEME = '189060841857';
  var SEED = {
    '/pages/delivery-shipping#delivery-options': 'template--27514158776705__carriers',
    '/pages/click-and-collect#how-it-works': 'template--27515727905153__steps',
    '/pages/hq-rewards#how-hq-rewards-works': 'template--27523040838017__process',
    '/pages/visit-our-ilkeston-store#store-opening-hours': 'template--27516114567553__info'
  };
  var LEARN = 'tdhq-peek-sections';
  var learned = {};
  try { learned = JSON.parse(window.localStorage.getItem(LEARN) || '{}') || {}; } catch (err) { learned = {}; }
  function sectionFor(key) { return learned[THEME + '|' + key] || (THEME === SEED_THEME ? SEED[key] : '') || ''; }
  function remember(key, id) {
    key = THEME + '|' + key;
    if (!id || learned[key] === id) return;
    learned[key] = id;
    try { window.localStorage.setItem(LEARN, JSON.stringify(learned)); } catch (err) {}
  }
  var parts = {};
  function loadFor(link) {
    var page = pageOf(link);
    var anchor = link.getAttribute('data-tdhq-peek');
    if (link.getAttribute('data-tdhq-peek-note')) return load(page);
    var key = page + '#' + anchor;
    if (parts[key]) return parts[key];
    var viaPage = function () {
      return load(page).then(function (doc) {
        var at = doc.getElementById(anchor);
        var wrap = at && at.closest ? at.closest('.shopify-section') : null;
        if (wrap && wrap.id) remember(key, wrap.id.replace(/^shopify-section-/, ''));
        return doc;
      });
    };
    var id = sectionFor(key);
    parts[key] = (id ? fetch(page + (page.indexOf('?') < 0 ? '?' : '&') + 'section_id=' + encodeURIComponent(id), { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        if (!doc.getElementById(anchor)) throw new Error('moved');
        return doc;
      })
      .catch(function () { delete learned[THEME + '|' + key]; return viaPage(); }) : viaPage());
    parts[key].catch(function () { delete parts[key]; });
    return parts[key];
  }
  function warm(e) {
    var link = e.target && e.target.closest && e.target.closest('[data-tdhq-peek]');
    if (link && link.getAttribute('href')) loadFor(link);
  }
  document.addEventListener('pointerover', warm, { passive: true });
  document.addEventListener('touchstart', warm, { passive: true });
  document.addEventListener('focusin', warm);
  /* the top-bar peeks (delivery, Click and Collect) are on every page: their
     sections are fetched once the page is idle, so a tap opens them at once */
  function warmBar() {
    var c = navigator.connection;
    if (c && c.saveData) return;
    Array.prototype.forEach.call(document.querySelectorAll('.tdhq-announce [data-tdhq-peek]'), function (a) {
      var key = pageOf(a) + '#' + a.getAttribute('data-tdhq-peek');
      if (sectionFor(key)) loadFor(a);
    });
  }
  var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 1500); };
  if (document.readyState === 'complete') idle(warmBar);
  else window.addEventListener('load', function () { idle(warmBar); }, { once: true });

  var pending = 0;
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('[data-tdhq-peek]');
    if (!link || e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var href = link.getAttribute('href');
    if (!href) return;
    e.preventDefault();
    if (!dlg) build();
    var page = pageOf(link);
    var anchor = link.getAttribute('data-tdhq-peek');
    var noteAnchor = link.getAttribute('data-tdhq-peek-note');
    var mine = ++pending;
    link.setAttribute('aria-busy', 'true');
    link.classList.add('is-peek-busy');
    loadFor(link).then(function (doc) {
      if (mine !== pending) return;
      var out = render(doc, anchor, noteAnchor);
      if (!out) throw new Error('section');
      opener = link;
      titleEl.textContent = out.heading || link.getAttribute('data-tdhq-peek-title') || link.textContent.trim();
      fullEl.setAttribute('href', href);
      bodyEl.textContent = '';
      bodyEl.appendChild(out.content);
      root.classList.add('tdhq-peek-open');
      if (!dlg.open) dlg.showModal();
      bodyEl.scrollTop = 0;
    }).catch(function () {
      if (mine === pending) window.location.href = href;
    }).then(function () {
      link.removeAttribute('aria-busy');
      link.classList.remove('is-peek-busy');
    });
  });
})();
